import {Badge} from '@mantine/core';
import React from 'react';

import ConnectorIcon from 'llm/chat-library/connectors/ConnectorIcon';
import ConnectorStatusBadge from 'llm/chat-library/connectors/ConnectorStatusBadge';
import type {ListItemViewModel} from 'llm/chat-library/shared/list-detail/listDetailTypes';
import type {MCPServerConfig, MCPServerUserConfig} from 'llm/chat-library/sidepanel/AISettingsConnectors';

/**
 * Maps AI Connector domain objects into the shared list row view-model. Connectors have three row
 * shapes — connected, available, and awaiting setup — so, unlike the single-shape skills adapter,
 * this exposes one mapper per shape. All per-connector behavior (which action a row surfaces, its
 * status) lives here as data + closures so the shared row component stays generic.
 *
 * Connectors have no detail page, so the Connectors listing renders these rows as static (it omits
 * the listing view's `onSelectItem`); every action lives in the trailing button, badge, or menu.
 */

/** A connector the user hasn't connected yet but can: shows a "Connect" button. */
export function toAvailableConnectorListItem(
  config: MCPServerConfig,
  handlers: {onConnect: (config: MCPServerConfig) => void}
): ListItemViewModel {
  return {
    id: config.id,
    icon: <ConnectorIcon extensionDefinitionId={config.extensionDefinitionId} iconUrl={config.iconUrl} />,
    title: config.name,
    description: config.description ?? undefined,
    primaryAction: {
      label: 'Connect',
      ariaLabel: `Connect ${config.name}`,
      onClick: () => {
        handlers.onConnect(config);
      },
    },
  };
}

/** A connector the user has connected: shows live status plus a manage/disconnect menu. */
export function toConnectedConnectorListItem(
  userConfig: MCPServerUserConfig,
  handlers: {
    onManageTools: (config: MCPServerUserConfig) => void;
    onDisconnect: (config: MCPServerUserConfig) => void;
  }
): ListItemViewModel {
  const {id: serverId, name, description, extensionDefinitionId, iconUrl} = userConfig.mcpServerConfig;
  return {
    id: userConfig.id,
    icon: <ConnectorIcon extensionDefinitionId={extensionDefinitionId} iconUrl={iconUrl} />,
    title: name,
    description: description ?? undefined,
    badge: <ConnectorStatusBadge serverId={serverId} />,
    menu: [
      {
        items: [
          {
            name: 'Manage tools',
            onSelect: () => {
              handlers.onManageTools(userConfig);
            },
          },
          {
            name: 'Disconnect',
            variant: 'destructive',
            onSelect: () => {
              handlers.onDisconnect(userConfig);
            },
          },
        ],
      },
    ],
  };
}

/** A connector installed but not yet usable: an admin must grant tool access first. */
export function toAwaitingSetupConnectorListItem(config: MCPServerConfig): ListItemViewModel {
  return {
    id: config.id,
    icon: <ConnectorIcon extensionDefinitionId={config.extensionDefinitionId} iconUrl={config.iconUrl} />,
    title: config.name,
    description: config.description ?? undefined,
    // Surface the required admin action as a visible subtitle rather than hover-only tooltip content,
    // so keyboard and screen-reader users can discover it (the badge trigger isn't focusable).
    subtitle: 'An admin needs to grant tool access before this connector can be connected',
    badge: (
      <Badge color="gray" size="xs" variant="light" radius="sm">
        Setup required
      </Badge>
    ),
  };
}
